(() => {
  const containerSelector = '#ProductGridContainer';
  const collectionSelector = '.collection[data-section-id]';
  const productSelector = '#product-grid > [data-product-id]';

  let requestController;
  let scheduled = false;

  const getCollection = () => document.querySelector(`${containerSelector} ${collectionSelector}`);

  const getRequestKey = (collection) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('page', collection.dataset.currentPage);

    return [
      url.toString(),
      collection.dataset.sectionId,
      collection.dataset.totalPages,
      collection.dataset.productsPerPage,
    ].join('|');
  };

  const getPageProducts = (root) =>
    Array.from(root.querySelectorAll(productSelector), (product) => product.cloneNode(true));

  const fetchPageProducts = async (page, sectionId, signal) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      throw new Error(`Unable to load collection page ${page} (${response.status})`);
    }

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const collection = documentFragment.querySelector(`${containerSelector} ${collectionSelector}`);

    if (!collection) {
      throw new Error(`Collection markup was missing from page ${page}`);
    }

    return getPageProducts(collection);
  };

  const orderCollection = async () => {
    const collection = getCollection();
    if (!collection) return;

    const grid = collection.querySelector('#product-grid');
    const currentPage = Number.parseInt(collection.dataset.currentPage, 10);
    const totalPages = Number.parseInt(collection.dataset.totalPages, 10);
    const productsPerPage = Number.parseInt(collection.dataset.productsPerPage, 10);

    if (
      !grid ||
      !Number.isInteger(currentPage) ||
      !Number.isInteger(totalPages) ||
      !Number.isInteger(productsPerPage) ||
      totalPages < 1 ||
      productsPerPage < 1
    ) {
      return;
    }

    const requestKey = getRequestKey(collection);
    if (collection.dataset.stockOutLastRequest === requestKey) return;

    if (requestController) requestController.abort();
    requestController = new AbortController();
    const { signal } = requestController;

    try {
      const pageRequests = [];

      for (let page = 1; page <= totalPages; page += 1) {
        pageRequests.push(
          page === currentPage
            ? Promise.resolve(getPageProducts(collection))
            : fetchPageProducts(page, collection.dataset.sectionId, signal)
        );
      }

      const pages = await Promise.all(pageRequests);
      if (signal.aborted) return;

      const activeCollection = getCollection();
      if (!activeCollection || getRequestKey(activeCollection) !== requestKey) return;

      const uniqueProducts = new Map();
      pages.flat().forEach((product) => {
        const { productId } = product.dataset;
        if (productId && !uniqueProducts.has(productId)) uniqueProducts.set(productId, product);
      });

      const products = Array.from(uniqueProducts.values());
      const availableProducts = products.filter((product) => product.dataset.productAvailable === 'true');
      const soldOutProducts = products.filter((product) => product.dataset.productAvailable !== 'true');
      const orderedProducts = availableProducts.concat(soldOutProducts);
      const pageStart = (currentPage - 1) * productsPerPage;
      const requestedProducts = orderedProducts.slice(pageStart, pageStart + productsPerPage);
      const activeGrid = activeCollection.querySelector('#product-grid');

      if (!activeGrid) return;

      activeCollection.dataset.stockOutLastRequest = requestKey;
      requestedProducts.forEach((product, index) => {
        if (product.hasAttribute('data-cascade')) {
          product.style.setProperty('--animation-order', index + 1);
        }
      });
      activeGrid.replaceChildren(...requestedProducts);

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger(activeGrid);
      }

      activeCollection.classList.remove('stock-order-pending');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Sold-out product ordering was skipped; Shopify pagination remains unchanged.', error);
        const activeCollection = getCollection();
        if (activeCollection) activeCollection.classList.remove('stock-order-pending');
      }
    }
  };

  const scheduleOrdering = () => {
    if (scheduled) return;
    scheduled = true;

    window.requestAnimationFrame(() => {
      scheduled = false;
      orderCollection();
    });
  };

  const start = () => {
    const productGridContainer = document.querySelector(containerSelector);
    if (!productGridContainer) return;

    new MutationObserver(scheduleOrdering).observe(productGridContainer, {
      childList: true,
      subtree: true,
    });

    scheduleOrdering();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
